import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useFormikContext } from 'formik';
import { IconInfo } from 'components/Icons';
import { formContainer, inputContainer } from 'modules/Models/components/ModelForm/styles';
import { FieldValueMapType, FormikInitialValues } from 'modules/Models/components/ModelForm/utils/types';
import { usePlatformResourceSelector, OptionValue } from 'components/PlatformResourceSelector/usePlatformResourceSelector';
import { ActionMeta, SingleValue } from 'react-select';
import { selectModelTypeFieldTypeInfo, DATASET_ID_PATH, DATASET_VERSION_ID_PATH } from 'modules/Models/components/ModelForm/utils/utils';
import { PlatformResourceSelector } from 'components/PlatformResourceSelector';
import { PlatformResourceVersionSelector } from 'components/PlatformResourceVersionSelector/PlatformResourceVersionSelector';
import { getDatasetVersionMetricsTE } from 'api/datasets/getDatasetVersionMetrics';
import { useQueryTE } from 'utils/react-query';
import { either, pipe, errorToReactLeft } from 'utils/fp';
import { OptionVersionValue, usePlatformResourceVersionSelector } from 'components/PlatformResourceVersionSelector/usePlatformResourceVersionSelector';
import { useDataFromResource } from 'components/PlatformResourceSelector/utils';
import _get from 'lodash/get';
import { useDatasetVersionsList } from 'modules/Datasets/hooks/useListDatasetVersions';
import { FormLabel } from '../FormLabel';
import { selectorContainer, helpText } from './DatasetValidationFormElement.styled';
type DatasetValidationProps = React.InputHTMLAttributes<HTMLInputElement> & {
  path: string;
};
export const DatasetValidation = ({
  path
}: DatasetValidationProps) => {
  const {
    query
  } = (useRouter() as {
    query: Record<string, string>;
  });
  const {
    appId,
    userOrOrgId
  } = query;
  const {
    values,
    setFieldValue
  } = useFormikContext();
  const {
    fieldValueMap
  } = (values as {
    fieldValueMap: FieldValueMapType<string>;
  });
  const datasetId = fieldValueMap[DATASET_ID_PATH] || '';
  const datasetVersionId = fieldValueMap[DATASET_VERSION_ID_PATH] || '';
  const {
    description = ''
  } = selectModelTypeFieldTypeInfo(path, (values as FormikInitialValues));
  const onDatasetIdChange = useCallback((option: SingleValue<OptionValue>, meta?: ActionMeta<OptionValue>) => {
    if (meta?.action === 'clear') {
      setFieldValue('fieldValueMap', {
        ...fieldValueMap,
        [DATASET_ID_PATH]: '',
        [DATASET_VERSION_ID_PATH]: ''
      });
      return;
    }
    setFieldValue('fieldValueMap', {
      ...fieldValueMap,
      [DATASET_ID_PATH]: option?.label,
      [DATASET_VERSION_ID_PATH]: ''
    });
  }, [fieldValueMap]);
  const onDatasetVersionIdChange = useCallback((option: SingleValue<OptionVersionValue>, meta?: ActionMeta<OptionVersionValue>) => {
    if (meta?.action === 'clear') return;
    setFieldValue('fieldValueMap', {
      ...fieldValueMap,
      [DATASET_VERSION_ID_PATH]: option?.label || ''
    });
  }, [fieldValueMap]);
  const datasetsData = useDataFromResource({
    appContextUserId: userOrOrgId,
    appId,
    type: 'datasets',
    customFilter: data => data.app_id === appId
  });
  const prsData = usePlatformResourceSelector(datasetsData, datasetId);
  const datasetVersionsList = useDatasetVersionsList({
    appId,
    userId: userOrOrgId,
    datasetId,
    notFailed: true
  });
  const prvsData = usePlatformResourceVersionSelector({
    type: 'datasets',
    data: datasetVersionsList.data,
    loading: datasetVersionsList.isLoading,
    selected: datasetVersionId || undefined
  });
  return <div className={formContainer}>
      <div className={inputContainer}>
        <FormLabel path={path} description={description} required />
        <div className={selectorContainer}>
          <PlatformResourceSelector {...prsData} onChange={onDatasetIdChange} placeholder="Default Dataset (All 0 inputs)" isClearable />
          {!prsData.isLoading && !prvsData.loading && !!prvsData.options.length && <PlatformResourceVersionSelector copyIdEnabled isLoading={prvsData.loading} onChange={onDatasetVersionIdChange} options={prvsData.options} type={prvsData.type} value={prvsData.selected} />}
          {!prsData.isLoading && !prvsData.loading && !!datasetId.length && (!!datasetVersionId || !datasetVersionId && !datasetVersionsList.data.length) && <InformationMessage appId={appId} datasetId={datasetId} datasetVersionId={datasetVersionId} datasetVersions={datasetVersionsList.data} userOrOrgId={userOrOrgId} />}
        </div>
      </div>
    </div>;
};
const getConceptsCountFromDatasetVersion = ({
  appId,
  userOrOrgId,
  datasetId,
  datasetVersionId
}: {
  appId: string;
  userOrOrgId: string;
  datasetId: string;
  datasetVersionId: string;
}) => {
  const [count, setCount] = useState(0);
  const {
    data: datasetVersionMetrics10
  } = useQueryTE(['DatasetVersionMetrics', {
    appId,
    userId: userOrOrgId,
    datasetId,
    datasetVersionId,
    typeId: 10
  }], getDatasetVersionMetricsTE({
    appId,
    userId: userOrOrgId,
    datasetId,
    datasetVersionId,
    typeId: 10 // type 10 is for concepts only
  }, errorToReactLeft), {
    enabled: Boolean(userOrOrgId) && Boolean(datasetId) && Boolean(datasetVersionId)
  });
  useEffect(() => {
    if (datasetVersionMetrics10) {
      pipe(datasetVersionMetrics10, either.fold(() => {}, resp => setCount(resp.dataset_version_metrics_groups.length || 0)));
    }
  }, [datasetVersionMetrics10]);
  return count;
};
const getInputsCountFromDatasetVersion = (datasetsList: CF.API.Datasets.DatasetVersion[], selectedId: string) => {
  const selectedDataset = datasetsList.find(dataset => dataset.id === selectedId);
  return Number(_get(selectedDataset, 'metrics./.inputs_count', 0));
};
const InformationMessage = ({
  appId,
  datasetId,
  datasetVersionId,
  datasetVersions,
  userOrOrgId
}: {
  appId: string;
  datasetId: string;
  datasetVersionId: string;
  datasetVersions: CF.API.Datasets.DatasetVersion[];
  userOrOrgId: string;
}) => {
  const versionsCount = datasetVersions.length;
  const inputsCount = getInputsCountFromDatasetVersion(datasetVersions, datasetVersionId);
  const conceptsCount = getConceptsCountFromDatasetVersion({
    appId,
    userOrOrgId,
    datasetId,
    datasetVersionId
  });
  if (versionsCount > 0 && inputsCount > 0 && conceptsCount > 0) {
    return null;
  }
  const userOrOrgIdParam = encodeURIComponent(userOrOrgId);
  const appIdParam = encodeURIComponent(appId);
  const datasetIdParam = encodeURIComponent(datasetId);
  return <div className={helpText}>
      <div className="icon">
        <IconInfo size={18} />
      </div>
      {!(versionsCount > 0) ? <div>
          You need to{' '}
          <a href={`/${userOrOrgIdParam}/${appIdParam}/datasets/${datasetIdParam}`} rel="noreferrer">
            create a new version
          </a>{' '}
          of this dataset for training. If you&rsquo;ve
          <br /> already made or update a version refresh the list.
        </div> : !(inputsCount > 0) || !(conceptsCount > 0) ? <div className="text">
          You need to{' '}
          <a href={`/${userOrOrgIdParam}/${appIdParam}/inputs?datasetId=${datasetIdParam}`} rel="noreferrer">
            add labeled inputs
          </a>{' '}
          to this dataset for training, this dataset version does not have any present. Select another version or{' '}
          <a href={`/${userOrOrgIdParam}/${appIdParam}/datasets/${datasetIdParam}`} rel="noreferrer">
            create a new version
          </a>{' '}
          with labeled inputs added.
        </div> : null}
    </div>;
};